import { useQuery } from '@tanstack/react-query'
import React from 'react'
import type { MRT_PaginationState as MaterialPaginationState } from 'material-react-table'
import { useAlert } from 'contexts/AlertContext'
import { useCompanies } from 'contexts/CompaniesContext'
import { useTranslator } from 'contexts/StatusTranslatorContext'
import { UniversalVehicle } from 'models/universalData'
import { fetchVehiclesByEntityBatches } from 'utils/api/fetchVehiclesByEntityBatches'
import { currentQueryService } from 'utils/api/queries'
import { extractUniversalVehicles } from 'utils/helpers/extractViewModels'

const DEFAULT_EXPORT_PAGE_SIZE = 50

const SUPPORTED_TABLES = ['stock', 'waitingFiles', 'search'] as const

type SupportedTableKey = (typeof SUPPORTED_TABLES)[number]

type TableKeyToDataMap = {
  stock: UniversalVehicle
  waitingFiles: UniversalVehicle
  search: UniversalVehicle
}

type UsePaginatedVehiclesOptions = {
  searchKey?: 'no' | 'licensePlate' | 'vin'
  searchValue?: string
  enabled?: boolean
  initialPageSize?: number
}

type PaginationState = MaterialPaginationState

type PaginationUpdater = PaginationState | ((prev: PaginationState) => PaginationState)

type PaginatedHookResult<K extends SupportedTableKey> = {
  data: Array<TableKeyToDataMap[K]>
  totalCount: number
  pagination: PaginationState
  setPagination: (updater: PaginationUpdater) => void
  isLoading: boolean
  isFetching: boolean
  isError: boolean
  fetchAllPages: (
    pageSize?: number,
    onProgress?: (info: { loaded: number; total: number }) => void,
    signal?: AbortSignal,
  ) => Promise<Array<TableKeyToDataMap[K]>>
}

type PageResult<T> = {
  items: Array<T>
  totalCount: number
}

const buildPaginationState = (pageSize: number): PaginationState => ({
  pageIndex: 0,
  pageSize,
})

const sanitizeIds = (ids: Array<string | undefined | null>) =>
  ids.filter((id): id is string => Boolean(id && id.length > 0))

const buildVehicleDedupeKey = (vehicle: {
  no: string
  entityId: string
  contactId: string | null
}) => {
  if (vehicle.no) {
    return vehicle.no
  }

  const rawVehicle = vehicle as unknown as {
    id?: unknown
    vehiclePartial?: { licensePlate?: unknown; vin?: unknown }
  }
  const rawId = rawVehicle.id
  if (typeof rawId === 'string' && rawId.length > 0) {
    return `id:${rawId}`
  }
  if (typeof rawId === 'number') {
    return `id:${rawId}`
  }

  const licensePlate =
    typeof rawVehicle.vehiclePartial?.licensePlate === 'string'
      ? rawVehicle.vehiclePartial.licensePlate
      : ''
  const vin =
    typeof rawVehicle.vehiclePartial?.vin === 'string' ? rawVehicle.vehiclePartial.vin : ''

  return `fallback:${vehicle.entityId || 'unknown'}:${vehicle.contactId || 'unknown'}:${
    licensePlate
  }:${vin}`
}

function usePaginatedVehicles<K extends SupportedTableKey>(
  tableKey: K,
  options?: UsePaginatedVehiclesOptions,
): PaginatedHookResult<K> {
  const { filteringCompaniesIds } = useCompanies()
  const { translateStatusVehicleArray } = useTranslator()
  const { displayAlert } = useAlert()

  const initialPageSize = options?.initialPageSize ?? 25
  const [pagination, setPaginationState] = React.useState<PaginationState>(
    buildPaginationState(initialPageSize),
  )

  const setPagination = (updater: PaginationUpdater) => {
    setPaginationState((prev) =>
      typeof updater === 'function'
        ? (updater as (value: PaginationState) => PaginationState)(prev)
        : updater,
    )
  }

  const idsKey = React.useMemo(() => filteringCompaniesIds.join(','), [filteringCompaniesIds])
  const searchKey = options?.searchKey ?? 'licensePlate'
  const hasSearchValue =
    tableKey !== 'search' || Boolean(options?.searchValue && options?.searchValue.length > 0)
  const sanitizedIds = React.useMemo(
    () => sanitizeIds(filteringCompaniesIds),
    [filteringCompaniesIds],
  )
  const isEnabled = sanitizedIds.length > 0 && hasSearchValue && (options?.enabled ?? true)

  React.useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }))
  }, [idsKey, options?.searchValue, tableKey])

  const fetchVehiclesPage = React.useCallback(
    async (
      pageIndex: number,
      pageSize: number,
      signal?: AbortSignal,
    ): Promise<PageResult<UniversalVehicle>> => {
      if (!isEnabled) return { items: [], totalCount: 0 }

      const fetcher = (batch: Array<string>, batchSignal?: AbortSignal) => {
        const requestSignal = batchSignal ?? signal
        if (tableKey === 'stock') {
          return currentQueryService.getStockFromEntities(batch, {
            page: pageIndex,
            limit: pageSize,
            signal: requestSignal,
          })
        }
        if (tableKey === 'waitingFiles') {
          return currentQueryService.getWaitingFilesFromEntities(batch, {
            page: pageIndex,
            limit: pageSize,
            signal: requestSignal,
          })
        }
        return currentQueryService.getVehicles(batch, searchKey, options?.searchValue ?? '', {
          page: pageIndex,
          limit: pageSize,
          signal: requestSignal,
        })
      }

      const { items, totalCount } = await fetchVehiclesByEntityBatches(sanitizedIds, fetcher, {
        dedupeKey: buildVehicleDedupeKey,
        maxItems: pageSize,
        signal,
      })

      const translated = translateStatusVehicleArray(items)
      const viewModels = extractUniversalVehicles(translated)
      return { items: viewModels, totalCount }
    },
    [
      isEnabled,
      options?.searchValue,
      sanitizedIds,
      searchKey,
      tableKey,
      translateStatusVehicleArray,
    ],
  )

  const fetchPage = React.useCallback(
    async (
      pageIndex: number,
      pageSize: number,
      signal?: AbortSignal,
    ): Promise<PageResult<TableKeyToDataMap[K]>> =>
      fetchVehiclesPage(pageIndex, pageSize, signal) as Promise<PageResult<TableKeyToDataMap[K]>>,
    [fetchVehiclesPage],
  )

  const queryKey = React.useMemo(
    () => [
      'usePaginatedVehicles',
      tableKey,
      sanitizedIds,
      pagination.pageIndex,
      pagination.pageSize,
      options?.searchValue,
      searchKey,
    ],
    [
      options?.searchValue,
      pagination.pageIndex,
      pagination.pageSize,
      sanitizedIds,
      searchKey,
      tableKey,
    ],
  )

  const query = useQuery<PageResult<TableKeyToDataMap[K]>>({
    queryKey,
    enabled: isEnabled,
    placeholderData: (previousData) => previousData,
    queryFn: ({ signal }) => fetchPage(pagination.pageIndex, pagination.pageSize, signal),
    staleTime: 5 * 60 * 1000,
  })

  React.useEffect(() => {
    if (query.isError) {
      displayAlert('Erreur lors du chargement des données', 'error')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.isError])

  const data = query.data?.items ?? []
  const totalCount = query.data?.totalCount ?? 0

  const fetchAllPages = React.useCallback(
    async (
      pageSize?: number,
      onProgress?: (info: { loaded: number; total: number }) => void,
      signal?: AbortSignal,
    ) => {
      const effectivePageSize = pageSize ?? DEFAULT_EXPORT_PAGE_SIZE
      if (!isEnabled) return [] as Array<TableKeyToDataMap[K]>
      const createAbortError = () => {
        if (signal?.reason instanceof Error) return signal.reason
        const abortError = new Error('Aborted')
        abortError.name = 'AbortError'
        return abortError
      }

      const throwIfAborted = () => {
        if (signal?.aborted) {
          throw createAbortError()
        }
      }

      const firstPage = await fetchPage(0, effectivePageSize, signal)
      throwIfAborted()
      const totalPages = Math.ceil(firstPage.totalCount / effectivePageSize)
      onProgress?.({ loaded: firstPage.items.length, total: firstPage.totalCount })
      const remainingIndexes = Array.from(
        { length: Math.max(0, totalPages - 1) },
        (_, idx) => idx + 1,
      )

      const aggregated = await remainingIndexes.reduce<Promise<Array<TableKeyToDataMap[K]>>>(
        async (accPromise, pageIndex) => {
          const acc = await accPromise
          throwIfAborted()
          const next = await fetchPage(pageIndex, effectivePageSize, signal)
          onProgress?.({ loaded: acc.length + next.items.length, total: firstPage.totalCount })
          return acc.concat(next.items)
        },
        Promise.resolve([...firstPage.items] as Array<TableKeyToDataMap[K]>),
      )

      throwIfAborted()
      return aggregated
    },
    [fetchPage, isEnabled],
  )

  return {
    data,
    totalCount,
    pagination,
    setPagination,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    fetchAllPages,
  }
}

export { usePaginatedVehicles }
